import * as yup from 'yup'

import {
  EVENT_FORM_CONTEXT_IDENTITY_DOCUMENT_EXEMPT_ATTENDEE_IDS,
  EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED,
  EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED,
  EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED,
  EVENT_FORM_CONTEXT_IS_ZOOM_PARTICIPANTS_MAPPING_REQUIRED,
} from './formContext'

import { ROLES_WITH_ID } from '@/components/Event/NewScript/constants'
import {
  identityDocumentSchema,
  STATUS_NOT_PRESENT_OR_STIPULATE,
} from '@/components/Event/NewScript/schemas/identityDocumentSchema'
import { zoomParticipantSchema } from '@/components/Event/NewScript/schemas/zoomParticipantSchema'
import {
  AttendeeRole,
  IdentityDocumentStatus,
  IdentityDocumentType,
  PHOTO_ID_VERIFIED_LABEL,
  TurnaroundHours,
} from '@/constants'

const sharedIdentityDocumentFields = {
  status: yup
    .mixed<IdentityDocumentStatus>()
    .oneOf(Object.values(IdentityDocumentStatus))
    .required('Status is required'),
  document_type: yup
    .mixed<IdentityDocumentType>()
    .oneOf(Object.values(IdentityDocumentType))
    .when('status', ([status], nestedSchema) => {
      if (
        STATUS_NOT_PRESENT_OR_STIPULATE.includes(
          status as IdentityDocumentStatus,
        )
      ) {
        return nestedSchema.optional().nullable()
      }
      return nestedSchema.required('Document type is required')
    }),
}

export const EMAIL_REGEXP =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const attendeeSchema = yup.object({
  attendee_id: yup.string().required(),
  name: yup
    .string()
    .trim()
    .required('Name is required')
    .max(255, 'Name must have at most 255 characters')
    .test(
      'capitalized',
      'Please enter correctly capitalized name',
      (value: string) => {
        const words = value.split(/\s+/)
        return words.every(word => /(^&$)|\p{Uppercase_Letter}/u.test(word))
      },
    ),
  transcript_order: yup
    .string()
    .oneOf(Object.values(TurnaroundHours).map(t => t.toString()))
    .nullable()
    .default(null),
  email: yup
    .string()
    // Absorbed here rather than by writing the trimmed value back into the
    // input, which two separate components used to do to the same field.
    .transform(value => (typeof value === 'string' ? value.trim() : value))
    .max(255, 'Email must have at most 255 characters')
    .test('email', 'Email is required', (value, context) => {
      if (!value) {
        return true
      }

      if (!EMAIL_REGEXP.test(value)) {
        return context.createError({ message: 'Please enter valid email' })
      }

      return true
    })
    .when('transcript_order', {
      is: (value: number | null) => value !== null,
      then: schema =>
        schema.required(
          'Email is required if attendee wants to order transcript',
        ),
      otherwise: schema => schema.nullable(),
    }),
  role: yup
    .mixed<AttendeeRole>()
    .oneOf(Object.values(AttendeeRole))
    .required('Role is required'),
  present: yup.boolean().nullable().notOneOf([null], 'Joined Zoom is required'),
  zoom_participants: yup
    .array()
    .of(zoomParticipantSchema)
    .when(
      [
        'present',
        `$${EVENT_FORM_CONTEXT_IS_ZOOM_PARTICIPANTS_MAPPING_REQUIRED}`,
      ],
      ([present, isZoomParticipantsMappingRequired], schema) => {
        if (present && isZoomParticipantsMappingRequired) {
          return schema
            .min(
              1,
              'Please select at least one Zoom user name for this attendee',
            )
            .required('Please select Zoom user name for this attendee')
        }
        return schema.default([])
      },
    ),
  identity_document: identityDocumentSchema
    .nullable()
    .default(null)
    .when(
      [
        `$${EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED}`,
        `$${EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED}`,
        'role',
        'attendee_id',
        `$${EVENT_FORM_CONTEXT_IDENTITY_DOCUMENT_EXEMPT_ATTENDEE_IDS}`,
      ],
      (
        [
          _isDocumentNumberRequired,
          isIdentityDocumentRequired,
          role,
          attendeeId,
          identityDocumentExemptAttendeeIds,
        ],
        schema,
      ) => {
        const isIdentityDocumentExempt = (
          (identityDocumentExemptAttendeeIds as string[] | undefined) ?? []
        ).includes(attendeeId as string)

        // On Review and Opening steps, require identity_document for WITNESS and GUARDIAN roles
        if (
          isIdentityDocumentRequired &&
          ROLES_WITH_ID.includes(role as AttendeeRole) &&
          !isIdentityDocumentExempt
        ) {
          return schema
            .shape({
              ...sharedIdentityDocumentFields,
              photo_id_verified: yup.boolean().when('status', ([status]) => {
                if (
                  STATUS_NOT_PRESENT_OR_STIPULATE.includes(
                    status as IdentityDocumentStatus,
                  )
                ) {
                  return yup.boolean().default(false).optional()
                }

                return yup
                  .boolean()
                  .oneOf([true], `${PHOTO_ID_VERIFIED_LABEL} is required`)
                  .required(`${PHOTO_ID_VERIFIED_LABEL} is required`)
              }),
            })
            .required('Identity document is required')
        }

        // this is so that validation still passes for non required identity documents even if it's not null but empty
        if (isIdentityDocumentRequired === false || isIdentityDocumentExempt) {
          return schema.transform(
            (value: yup.InferType<typeof identityDocumentSchema> | null) =>
              value &&
              value.status === null &&
              value.document_type === null &&
              !value.photo_id_verified
                ? null
                : value,
          )
        }

        return schema
      },
    ),
  city: yup
    .string()
    .when([`$${EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED}`, 'role'], {
      is: (isWitnessLocationRequired: boolean, role: string) =>
        isWitnessLocationRequired && role === AttendeeRole.WITNESS,
      then: schema => schema.required('City is required'),
      otherwise: schema => schema.nullable(),
    }),
  state: yup
    .string()
    .when([`$${EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED}`, 'role'], {
      is: (isWitnessLocationRequired: boolean, role: string) =>
        isWitnessLocationRequired && role === AttendeeRole.WITNESS,
      then: schema => schema.required('State is required'),
      otherwise: schema => schema.nullable(),
    }),
})

// Uniqueness is a property of the attendee set, not of a single attendee, so it
// cannot live in attendeeSchema. This helper is the one comparison, called both
// from the array-level test below and from the attendee editor's commit
// boundary, where the edited attendee is merged into the stored set.
export const getDuplicateAttendeeEmailError = (
  email: string | null | undefined,
  otherEmails: (string | null | undefined)[],
): string | undefined => {
  if (!email) {
    return undefined
  }

  return otherEmails.some(otherEmail => otherEmail === email)
    ? `E-mail ${email} is duplicated, all attendees must have unique (or empty) e-mail`
    : undefined
}

export const attendeesFieldSchema = yup
  .array(attendeeSchema)
  .required()
  .min(1, 'At least one attendee is required')
  .test('witness', (value, context) => {
    const witnessCount = value.filter(
      attendee => attendee.role === AttendeeRole.WITNESS,
    ).length
    if (witnessCount !== 1) {
      return context.createError({
        path: 'attendees.global.witness',
        type: 'custom',
        message: 'There must be exactly one witness',
      })
    }
    return true
  })
  .test('unique-attendee-email', (value, ctx) => {
    const errors = value
      .map((attendee, idx) => {
        const message = getDuplicateAttendeeEmailError(
          attendee.email,
          value.filter((_, otherIdx) => otherIdx !== idx).map(a => a.email),
        )

        if (!message) {
          return null
        }

        return ctx.createError({
          path: `attendees.${idx}.email`,
          message,
        })
      })
      .filter((v): v is NonNullable<yup.ValidationError> => !!v)

    return errors.length > 0 ? new yup.ValidationError(errors) : true
  })
